/**
 * Makes React's reconciler resilient to browser auto-translation.
 *
 * Chrome / Google Translate rewrites text nodes and wraps each one in extra
 * `<font>` elements. React still holds references to the original nodes, so its
 * commit phase calls `removeChild` / `insertBefore` with nodes that have since
 * been re-parented by the translator. The DOM spec makes both throw
 * (`NotFoundError` / `HierarchyRequestError`), which takes down the whole
 * subtree — ~830 white-screens/day in `nushop-web` RUM (US #987145).
 *
 * The guard turns exactly that case into a no-op: when the node is not actually
 * a child of the target we return the node instead of throwing. Genuine app
 * errors are untouched — a correct DOM operation always satisfies these
 * pre-conditions, so the fast path is one identity comparison.
 *
 * `translate="no"` / `notranslate` is deliberately NOT used: customers rely on
 * browser translation because we don't offer multi-language sites.
 *
 * Must be installed before `hydrateRoot`.
 *
 * @see https://github.com/facebook/react/issues/11538
 * @see https://martijnhols.nl/blog/everything-about-google-translate-crashing-react
 */

let isInstalled = false;

/**
 * Logging is development-only on purpose: `console.error` is wrapped by the New
 * Relic browser agent (`wrapLogger`), so logging here in production would just
 * move the noise from JS errors to ERROR logs instead of removing it.
 */
const warn = (message: string, ...args: unknown[]) => {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(`[domTranslationGuard] ${message}`, ...args);
  }
};

export const installDomTranslationGuard = () => {
  if (isInstalled || typeof Node !== 'function' || !Node.prototype) {
    return;
  }
  isInstalled = true;

  const originalRemoveChild = Node.prototype.removeChild;
  const originalInsertBefore = Node.prototype.insertBefore;

  Node.prototype.removeChild = function guardedRemoveChild<T extends Node>(
    this: Node,
    child: T
  ): T {
    if (child.parentNode !== this) {
      warn('Cannot remove a child from a different parent', child, this);
      return child;
    }

    return originalRemoveChild.call(this, child);
  };

  Node.prototype.insertBefore = function guardedInsertBefore<T extends Node>(
    this: Node,
    node: T,
    child: Node | null
  ): T {
    // The reference node was re-parented (or removed) by the translator.
    if (child && child.parentNode !== this) {
      warn(
        'Cannot insert before a reference node from a different parent',
        child,
        this
      );
      return node;
    }

    // Would create a cycle — the translator moved `this` inside `node`.
    if (node !== this && node.contains(this)) {
      warn('Cannot insert a node into its own descendant', node, this);
      return node;
    }

    return originalInsertBefore.call(this, node, child);
  };
};
